import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { 
  User, Mail, Phone, FileText, Save, Lock, 
  Home, FolderOpen, Menu, X, LogOut, Bell,
  CheckCircle, Building, Linkedin, Twitter, Link2, Briefcase
} from 'lucide-react';
import { toast } from 'sonner';

const EvaluatorProfile = () => {
  const [evaluator, setEvaluator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [formData, setFormData] = useState({
    phone: '',
    bio: '',
    linkedin_url: '',
    twitter_url: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [assignmentsSummary, setAssignmentsSummary] = useState({ total: 0, completed: 0 });

  const navigate = useNavigate();
  const location = useLocation();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const evaluatorId = localStorage.getItem('user_id');

  useEffect(() => {
    // Auth check on mount
    const isAuthenticated = localStorage.getItem('cness_authenticated') === 'true';
    const userRole = localStorage.getItem('cness_user_role');
    const storedEvaluatorId = localStorage.getItem('user_id');
    
    if (!isAuthenticated || userRole !== 'evaluator' || !storedEvaluatorId) {
      sessionStorage.setItem('login_redirect', '/evaluator/profile');
      navigate('/login');
      return;
    }
    
    fetchEvaluatorData();
  }, []);

  const fetchEvaluatorData = async () => {
    try {
      setLoading(true);
      
      // Get user data from localStorage first
      const userData = localStorage.getItem('userData');
      if (userData) {
        const parsed = JSON.parse(userData);
        setEvaluator(parsed);
        setFormData({
          phone: parsed.phone || '',
          bio: parsed.bio || '',
          linkedin_url: parsed.linkedin_url || '',
          twitter_url: parsed.twitter_url || ''
        });
      }

      // Fetch assignments summary
      const response = await fetch(`${BACKEND_URL}/api/evaluator-assignments/${evaluatorId}`);
      const data = await response.json();
      const assignments = Array.isArray(data) ? data : (data.assignments || []);
      
      setAssignmentsSummary({
        total: assignments.length,
        completed: assignments.filter(a => a.status === 'completed').length
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/evaluator/profile/${evaluatorId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: formData.phone,
          bio: formData.bio,
          linkedin_url: formData.linkedin_url,
          twitter_url: formData.twitter_url
        })
      });

      if (response.ok) {
        toast.success('Profile updated successfully');
        // Update localStorage
        const updatedUser = { 
          ...evaluator, 
          phone: formData.phone, 
          bio: formData.bio,
          linkedin_url: formData.linkedin_url,
          twitter_url: formData.twitter_url
        };
        localStorage.setItem('userData', JSON.stringify(updatedUser));
        setEvaluator(updatedUser);
      } else {
        toast.error('Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Error updating profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordData.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/evaluator/change-password/${evaluatorId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        })
      });

      if (response.ok) {
        toast.success('Password changed successfully');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowPasswordForm(false);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error('Error changing password');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const evaluatorName = evaluator?.name || evaluator?.full_name || evaluator?.username || 'Evaluator';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-slate-800 text-white transition-all duration-300 flex flex-col`}>
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          {sidebarOpen && <span className="text-xl font-bold">CNESS</span>}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-slate-700 rounded-lg">
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            <li>
              <Link to="/evaluator/dashboard" className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${location.pathname === '/evaluator/dashboard' ? 'bg-indigo-600' : 'hover:bg-slate-700'}`}>
                <Home className="w-5 h-5" />
                {sidebarOpen && <span>Dashboard</span>}
              </Link>
            </li>
            <li>
              <Link to="/evaluator/organizations" className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${location.pathname === '/evaluator/organizations' ? 'bg-indigo-600' : 'hover:bg-slate-700'}`}>
                <FolderOpen className="w-5 h-5" />
                {sidebarOpen && <span>Assigned Organizations</span>}
              </Link>
            </li>
            <li>
              <Link to="/evaluator/profile" className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${location.pathname === '/evaluator/profile' ? 'bg-indigo-600' : 'hover:bg-slate-700'}`}>
                <User className="w-5 h-5" />
                {sidebarOpen && <span>My Profile</span>}
              </Link>
            </li>
          </ul>
        </nav>

        <div className="p-4 border-t border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center">
              <User className="w-5 h-5" />
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{evaluatorName}</p>
                <p className="text-xs text-slate-400">Evaluator</p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        <header className="bg-white shadow-sm border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
              <p className="text-sm text-gray-500">Manage your account settings</p>
            </div>
            <div className="flex items-center gap-4">
              <button className="p-2 hover:bg-gray-100 rounded-lg relative">
                <Bell className="w-5 h-5 text-gray-600" />
              </button>
              <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg" data-testid="logout-btn">
                <LogOut className="w-5 h-5" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 p-6 overflow-auto">
          <div className="max-w-3xl mx-auto space-y-6">
            {/* Profile Info Card */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-8">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center">
                    <User className="w-10 h-10 text-indigo-600" />
                  </div>
                  <div className="text-white">
                    <h2 className="text-2xl font-bold">{evaluatorName}</h2>
                    <p className="text-indigo-200">CNESS Evaluator</p>
                    {evaluator?.organization_affiliation && (
                      <p className="text-indigo-200 text-sm flex items-center gap-1 mt-1">
                        <Briefcase className="w-4 h-4" />
                        {evaluator.organization_affiliation}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-2 gap-6 mb-6">
                  <div className="bg-indigo-50 rounded-lg p-4 text-center">
                    <Building className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-gray-900">{assignmentsSummary.total}</p>
                    <p className="text-sm text-gray-500">Assigned Organizations</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-gray-900">{assignmentsSummary.completed}</p>
                    <p className="text-sm text-gray-500">Completed Evaluations</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Email (Read-only) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    <div className="flex items-center gap-3 px-4 py-3 bg-gray-100 rounded-lg">
                      <Mail className="w-5 h-5 text-gray-400" />
                      <span className="text-gray-600">{evaluator?.email || 'N/A'}</span>
                      <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Read-only</span>
                    </div>
                  </div>

                  {/* Phone (Editable) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <div className="flex items-center gap-3 px-4 py-3 border rounded-lg focus-within:ring-2 focus-within:ring-indigo-500">
                      <Phone className="w-5 h-5 text-gray-400" />
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="Enter your phone number"
                        className="flex-1 outline-none"
                        data-testid="phone-input"
                      />
                    </div>
                  </div>

                  {/* Bio (Editable) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Short Bio</label>
                    <div className="flex items-start gap-3 px-4 py-3 border rounded-lg focus-within:ring-2 focus-within:ring-indigo-500">
                      <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                      <textarea
                        value={formData.bio}
                        onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                        placeholder="Write a short bio about yourself..."
                        rows={3}
                        className="flex-1 outline-none resize-none"
                        data-testid="bio-input"
                      />
                    </div>
                  </div>

                  {/* Social Media Links */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn URL</label>
                      <div className="flex items-center gap-3 px-4 py-3 border rounded-lg focus-within:ring-2 focus-within:ring-indigo-500">
                        <Linkedin className="w-5 h-5 text-gray-400" />
                        <input
                          type="url"
                          value={formData.linkedin_url}
                          onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                          placeholder="https://linkedin.com/in/..."
                          className="flex-1 outline-none"
                          data-testid="linkedin-input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Twitter/X URL</label>
                      <div className="flex items-center gap-3 px-4 py-3 border rounded-lg focus-within:ring-2 focus-within:ring-indigo-500">
                        <Twitter className="w-5 h-5 text-gray-400" />
                        <input
                          type="url"
                          value={formData.twitter_url}
                          onChange={(e) => setFormData({ ...formData, twitter_url: e.target.value })}
                          placeholder="https://twitter.com/..."
                          className="flex-1 outline-none"
                          data-testid="twitter-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Credential Links (Read-only) */}
                  {evaluator?.credential_links && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Credential Links</label>
                      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-lg">
                        <Link2 className="w-5 h-5 text-gray-400" />
                        <span className="text-gray-600">{evaluator.credential_links}</span>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleSaveProfile}
                    disabled={saving}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50"
                    data-testid="save-profile-btn"
                  >
                    {saving ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <Save className="w-5 h-5" />
                    )}
                    Save Changes
                  </button>
                </div>
              </div>
            </div>

            {/* Password Change Card */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Lock className="w-5 h-5 text-gray-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Password Settings</h3>
                </div>
                <button
                  onClick={() => setShowPasswordForm(!showPasswordForm)}
                  className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                  data-testid="toggle-password-form"
                >
                  {showPasswordForm ? 'Cancel' : 'Change Password'}
                </button>
              </div>

              {showPasswordForm && (
                <div className="space-y-4 pt-4 border-t">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                    <input
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      data-testid="current-password-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                    <input
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      data-testid="new-password-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                    <input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      data-testid="confirm-password-input"
                    />
                  </div>
                  <button
                    onClick={handleChangePassword}
                    disabled={saving}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-800 text-white rounded-lg font-semibold hover:bg-slate-900 disabled:opacity-50"
                    data-testid="change-password-btn"
                  >
                    {saving ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <Lock className="w-5 h-5" />
                    )}
                    Update Password
                  </button>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default EvaluatorProfile;
